import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { InternalExternalLink } from '../../../types/InternalExternalLink'
import { PortableTextBodyElement } from '../../../utilities/blockContent'
import { isNullOrWhiteSpace } from '../../../utilities/string'
import { Button, ButtonTheme } from '../../Button'
import { Dropdown, DropdownOption } from '../../Forms/Dropdown'
import { ExternalIconSize, Link } from '../../Link'
import { Paragraph } from '../../Paragraph'
import { RichTextArea } from '../../PortableText/RichTextArea'
import { BasicBlockSerializer } from '../../PortableText/serializers/blocks/BasicBlockSerializer'
import { Case, Default, Switch } from '../../Switch'
import { AnnexOne, AnnexOneProps } from './AnnexOne'
import { CountrySelector } from './CountrySelector'
import { NoAccessNotice, NoAccessNoticeProps } from './NoAccessNotice'

type FormState = 'intro' | 'confirm-eligibility' | 'annex-one' | 'access-denied'

type Country = {
  eligibilityConfirmationText?: PortableTextBodyElement[]
  name: string
  nonProfessionalAccess: boolean
  nonProfessionalFullAccess: boolean
  professionalAccess: boolean
  professionalDisclaimerBody?: PortableTextBodyElement[]
  professionalDisclaimerTitle?: string
  simpleAccess?: boolean
  professionalRedirectAccessBody?: PortableTextBodyElement[]
  nonProfessionalRedirectAccessBody?: PortableTextBodyElement[]
}

export type OnAcceptDisclaimerCallbackProps = {
  accessiblePageUrl?: string
  country: string
  investorType?: string
}

export type OnAcceptDisclaimerCallback = (props: OnAcceptDisclaimerCallbackProps) => void

export interface TwoStepDisclaimerProps {
  annexOne: AnnexOneProps
  introduction: {
    countries: Country[]
    countriesIntro?: string
    disclaimerBody?: PortableTextBodyElement[]
    investorIntro?: string
    nonProfessionalInvestorBulletPoints?: string[]
    nonProfessionalInvestorLabel: string
    preamble?: string
    preambleLinks?: InternalExternalLink[]
    privacyPolicyLabel?: string
    privacyPolicyPageUrl?: string
    professionalInvestorBulletPoints?: string[]
    professionalInvestorLabel: string
    selectCountryLabel?: string
    selectCountryPlaceholder?: string
    selectInvestorLabel?: string
    selectInvestorPlaceholder?: string
    submitButtonLabel: string
    title?: string
  }
  noAccessNotice: NoAccessNoticeProps
  nonProfessionals: {
    accessiblePageUrl?: string
    confirmEligibilityButtonLabel: string
    declineEligibilityButtonLabel: string
    title?: string
  }
  onAccept?: OnAcceptDisclaimerCallback
  simpleVariant?: boolean
}

interface InvestorBulletPointsBoxProps {
  bulletPoints: string[]
  title?: string
  additionalClassName?: string
}

interface AdditionalDisclaimerBoxProps {
  body: PortableTextBodyElement[]
  title?: string
}

const InvestorBulletPointsBox = ({
  bulletPoints,
  title,
  additionalClassName = '',
}: InvestorBulletPointsBoxProps) => (
  <div
    className={`p-30 rounded-15 border border-neutral-lighter-alt mb-15 ${additionalClassName}`}
  >
    {!isNullOrWhiteSpace(title) && (
      <h2 className="mb-15 font-t-bold text-t-500 text-secondary-darker">{title}</h2>
    )}

    <ul className="list-disc pl-20 font-t-light text-t-300 leading-[145%] text-secondary-darkest">
      {bulletPoints.map((bullet, index) => (
        <li key={index}>{bullet}</li>
      ))}
    </ul>
  </div>
)

const AdditionalDisclaimerBox = ({ body, title }: AdditionalDisclaimerBoxProps) => (
  <div className="flex flex-col mb-45 p-30 gap-10 rounded-15 bg-shade">
    {!isNullOrWhiteSpace(title) && (
      <h2 className="font-t-bold text-t-500 text-secondary-darkest">{title}</h2>
    )}

    <div className="font-body text-body text-secondary-darkest">
      <RichTextArea
        components={{
          block: BasicBlockSerializer,
        }}
        value={body}
      />
    </div>
  </div>
)

export const TwoStepDisclaimer = ({
  annexOne,
  introduction,
  noAccessNotice,
  nonProfessionals,
  onAccept: onAcceptProps,
  simpleVariant,
}: TwoStepDisclaimerProps) => {
  const {
    countries,
    countriesIntro,
    disclaimerBody,
    investorIntro,
    nonProfessionalInvestorBulletPoints,
    nonProfessionalInvestorLabel,
    preamble,
    preambleLinks,
    privacyPolicyLabel,
    privacyPolicyPageUrl,
    professionalInvestorBulletPoints,
    professionalInvestorLabel,
    selectCountryLabel,
    selectCountryPlaceholder,
    selectInvestorLabel,
    selectInvestorPlaceholder,
    submitButtonLabel,
    title,
  } = introduction

  const {
    accessiblePageUrl,
    confirmEligibilityButtonLabel,
    declineEligibilityButtonLabel,
    title: eligibilityConfirmationTitle,
  } = nonProfessionals

  const [state, setState] = useState<FormState>('intro')
  const [selectedCountry, setSelectedCountry] = useState<Country>()
  const [selectedInvestorType, setSelectedInvestorType] = useState<string>()

  const onSelectCountry = useCallback(
    (countryName: string) => {
      const country = countries.find(country => country.name === countryName)
      setSelectedCountry(country)
    },
    [countries]
  )

  const onSelectInvestor = useCallback((investorType: string) => {
    setSelectedInvestorType(investorType)
  }, [])

  const onSubmit = useCallback(() => {
    let granted: boolean

    if (simpleVariant) {
      granted = !!selectedCountry?.simpleAccess
    } else {
      granted =
        (selectedInvestorType === 'professional' &&
          !!selectedCountry?.professionalAccess) ||
        (selectedInvestorType === 'non-professional' &&
          !!selectedCountry?.nonProfessionalAccess)
    }

    if (granted) {
      setState('annex-one')
    } else {
      setState('access-denied')
    }
  }, [selectedCountry, selectedInvestorType, simpleVariant])

  const onAccept = useCallback(() => {
    if (onAcceptProps && selectedCountry) {
      onAcceptProps({
        accessiblePageUrl:
          selectedInvestorType === 'non-professional' &&
          selectedCountry.nonProfessionalAccess &&
          !selectedCountry.nonProfessionalFullAccess
            ? accessiblePageUrl
            : undefined,
        country: selectedCountry.name,
        investorType: selectedInvestorType,
      })
    }
  }, [accessiblePageUrl, onAcceptProps, selectedCountry, selectedInvestorType])

  const onConfirmEligibility = useCallback(() => {
    setState('annex-one')
  }, [])

  const onDeclineEligibility = useCallback(() => {
    setState('access-denied')
  }, [])

  useEffect(() => {
    if (
      selectedInvestorType === 'non-professional' &&
      selectedCountry?.eligibilityConfirmationText
    ) {
      setState('confirm-eligibility')
    } else {
      setState('intro')
    }
  }, [selectedCountry, selectedInvestorType])

  const selectInvestorOptions: DropdownOption[] | undefined = useMemo(
    () =>
      !isNullOrWhiteSpace(professionalInvestorLabel) &&
      !isNullOrWhiteSpace(nonProfessionalInvestorLabel)
        ? [
            {
              label: professionalInvestorLabel ?? '',
              value: 'professional',
            },
            {
              label: nonProfessionalInvestorLabel ?? '',
              value: 'non-professional',
            },
          ]
        : undefined,
    [nonProfessionalInvestorLabel, professionalInvestorLabel]
  )

  const redirectedAccess = () =>
    (selectedInvestorType === 'non-professional' &&
      !!selectedCountry?.nonProfessionalRedirectAccessBody) ||
    (selectedInvestorType === 'professional' &&
      !!selectedCountry?.professionalRedirectAccessBody)

  return (
    <div className="my-30 tb:my-150">
      <Switch expr={state}>
        <Case value="annex-one">
          {/* Hidden inputs used by GTM at the final step */}
          <input id="select-country" type="hidden" value={selectedCountry?.name} />

          {!simpleVariant && (
            <input id="select-investor" type="hidden" value={selectedInvestorType} />
          )}

          <AnnexOne {...annexOne} onAccept={onAccept} />
        </Case>

        <Case value="access-denied">
          <NoAccessNotice {...noAccessNotice} />
        </Case>

        <Default>
          {!isNullOrWhiteSpace(title) && (
            <h1 className="mb-30 font-h2 text-h2 text-secondary-dark">{title}</h1>
          )}

          {!isNullOrWhiteSpace(preamble) && (
            <Paragraph
              className={classNames(
                'font-preamble text-preamble text-secondary-darker',
                {
                  'mb-45': !preambleLinks || preambleLinks.length === 0,
                },
                { 'mb-20': !!preambleLinks?.length }
              )}
            >
              {preamble}
            </Paragraph>
          )}

          {!!preambleLinks?.length && (
            <div className="flex flex-col gap-10 md:gap-5 mb-45">
              {preambleLinks.map((link, index) => (
                <div key={index}>
                  <Link
                    {...link}
                    className={classNames(
                      'text-secondary-darker font-preamble text-preamble border-b border-quinary transition-hove0r',
                      'hover:opacity-hover hover:border-opacity-50 hover:border-secondary-darker'
                    )}
                  >
                    {link.title}
                  </Link>
                </div>
              ))}
            </div>
          )}

          {!simpleVariant &&
            (!isNullOrWhiteSpace(investorIntro) ||
              !!professionalInvestorBulletPoints?.length ||
              !!nonProfessionalInvestorBulletPoints?.length ||
              (!isNullOrWhiteSpace(selectInvestorLabel) && !!selectInvestorOptions)) && (
              <>
                {!isNullOrWhiteSpace(investorIntro) && (
                  <Paragraph className="font-body text-body mb-20 text-secondary-darker">
                    {investorIntro}
                  </Paragraph>
                )}

                {!isNullOrWhiteSpace(selectInvestorLabel) && !!selectInvestorOptions && (
                  <div className="mb-15 p-30 rounded-15 bg-secondary-darker">
                    <label
                      className="block mb-10 text-shade-lightest-warm"
                      htmlFor="select-investor"
                    >
                      {selectInvestorLabel}
                    </label>

                    <Dropdown
                      name="select-investor"
                      onChange={onSelectInvestor}
                      options={selectInvestorOptions}
                      placeholder={selectInvestorPlaceholder}
                      value={selectedInvestorType}
                      variant="button"
                    />
                  </div>
                )}

                {!!nonProfessionalInvestorBulletPoints?.length && (
                  <InvestorBulletPointsBox
                    bulletPoints={nonProfessionalInvestorBulletPoints}
                    title={nonProfessionalInvestorLabel}
                  />
                )}

                {!!professionalInvestorBulletPoints?.length && (
                  <InvestorBulletPointsBox
                    bulletPoints={professionalInvestorBulletPoints}
                    title={professionalInvestorLabel}
                    additionalClassName="mb-45"
                  />
                )}

                <hr className="mb-45 h-[1px] bg-neutral-light" />
              </>
            )}

          {!isNullOrWhiteSpace(countriesIntro) && (
            <Paragraph className="font-body text-body mb-20 text-secondary-darker">
              {countriesIntro}
            </Paragraph>
          )}

          {!isNullOrWhiteSpace(selectCountryLabel) && (
            <CountrySelector
              additionalClassName={classNames({
                'mb-45': state === 'intro',
                'mb-20': state === 'confirm-eligibility',
              })}
              countries={countries.map(country => country.name)}
              label={selectCountryLabel}
              onSelectCountry={onSelectCountry}
              placeholder={selectCountryPlaceholder}
            />
          )}

          <div className="mb-45">
            {state === 'intro' && (
              <>
                {selectedInvestorType === 'professional' &&
                  !!selectedCountry?.professionalDisclaimerBody && (
                    <AdditionalDisclaimerBox
                      body={selectedCountry.professionalDisclaimerBody}
                      title={selectedCountry.professionalDisclaimerTitle}
                    />
                  )}

                {selectedInvestorType === 'professional' &&
                  !!selectedCountry?.professionalRedirectAccessBody && (
                    <AdditionalDisclaimerBox
                      body={selectedCountry.professionalRedirectAccessBody}
                    />
                  )}

                {selectedInvestorType === 'non-professional' &&
                  !!selectedCountry?.nonProfessionalRedirectAccessBody && (
                    <AdditionalDisclaimerBox
                      body={selectedCountry.nonProfessionalRedirectAccessBody}
                    />
                  )}

                <div className="flex gap-15">
                  {!isNullOrWhiteSpace(submitButtonLabel) && !redirectedAccess() && (
                    <Button
                      disabled={
                        (!simpleVariant && !selectedInvestorType) || !selectedCountry
                      }
                      onClick={onSubmit}
                      theme={ButtonTheme.PrimaryInverted}
                      type="button"
                    >
                      {submitButtonLabel}
                    </Button>
                  )}

                  {!isNullOrWhiteSpace(privacyPolicyLabel) &&
                    !isNullOrWhiteSpace(privacyPolicyPageUrl) && (
                      <Button
                        customAttributes={{
                          target: '_blank',
                        }}
                        externalIconSize={ExternalIconSize.None}
                        // Need to set this link as external - opening links in a new tab does not work with GatsbyLink
                        isExternalUrl={true}
                        theme={ButtonTheme.Primary}
                        url={privacyPolicyPageUrl}
                      >
                        {privacyPolicyLabel}
                      </Button>
                    )}
                </div>
              </>
            )}

            {!simpleVariant &&
              state === 'confirm-eligibility' &&
              selectedCountry?.eligibilityConfirmationText && (
                <>
                  <AdditionalDisclaimerBox
                    body={selectedCountry.eligibilityConfirmationText}
                    title={eligibilityConfirmationTitle}
                  />

                  <div className="flex flex-col items-start gap-15 tb:flex-row">
                    {!isNullOrWhiteSpace(confirmEligibilityButtonLabel) && (
                      <Button
                        onClick={onConfirmEligibility}
                        theme={ButtonTheme.PrimaryInverted}
                        type="button"
                      >
                        {confirmEligibilityButtonLabel}
                      </Button>
                    )}

                    {!isNullOrWhiteSpace(declineEligibilityButtonLabel) && (
                      <Button
                        onClick={onDeclineEligibility}
                        theme={ButtonTheme.Primary}
                        type="button"
                      >
                        {declineEligibilityButtonLabel}
                      </Button>
                    )}
                  </div>
                </>
              )}
          </div>

          <hr className="mb-45 h-[1px] bg-neutral-light" />

          {disclaimerBody && (
            <div
              className={classNames(
                'font-t-light text-t-300 leading-[145%] text-neutral-dark-alt',
                '[&>ul]:text-t-300 [&>ul]:font-t-light [&>ul]:leading-[145%] [&>ul]:text-neutral-dark-alt',
                '[&>ul>li:before]:text-neutral-dark-alt'
              )}
            >
              <RichTextArea
                components={{ block: BasicBlockSerializer }}
                value={disclaimerBody}
              />
            </div>
          )}
        </Default>
      </Switch>
    </div>
  )
}
